import * as React from 'react';
import { useState, useEffect } from 'react';
import { injectIntl, InjectedIntlProps, FormattedMessage, defineMessages } from 'react-intl';
import { History } from 'history';
import './styles.css';
import logoImage from '../../images/logo-sm-color.png';
import { SEARCH_PATH, SITE_URL, EMBED_PATH } from '../../constants';
import { get as getDatabase, IDatabase, format, getAnalyticsToken } from '../../services/database';
import ReactGA from 'react-ga';
import { PropagateLoader } from 'react-spinners';
import BraynSearch2 from './BraynSearch';
import { getSepcialPageFieldKeysForDataabse } from 'src/helpers/pages';
import {CommonSearchFormatter} from './layout/Common';

export default injectIntl(
    (
        props: InjectedIntlProps & {
            history: History;
            database: IDatabase | null;
            databaseName: string;
            accountName: string;
        }
    ) => {
        const [term, setTerm] = useState<string>('');
        const [location, setLocation] = useState<google.maps.places.PlaceResult | undefined>(undefined);
        const [distance, setDistance] = useState<number | null>(10);
        const [showFilters, setShowFilters] = useState<boolean>(true);
        const [selectedFilters, setSelectedFilters] = useState<object>({});
        const [code, setCode] = useState<string | null>(null);
        const [loading, setLoading] = useState<boolean>(false);
        const [mode, setMode] = useState<'address' | 'keyword'>('keyword')

        const database = props.database;
        const messages = defineMessages({
            searchPrompt: {
                id: 'search.form.placeholder',
                defaultMessage: 'Type in keywords or use filters to narrow down search...',
            },
            bryanSearchPrompt: {
                id: 'search.form.placeholder',
                defaultMessage: 'Search by Keyword or Address',
            },
        });

        const addTracker = async () => {
            if (!database || !database.name) return;
            const c = ((await getAnalyticsToken(database.name)) || '').replace(/"/g, '');
            setCode(c);
            ReactGA.addTrackers([
                {
                    trackingId: c,
                    gaOptions: { name: 'client' },
                },
            ]);
            ReactGA.pageview(`${database.title}`, ['harmonize', 'client']);
        };

        useEffect(() => {
            addTracker();
            // console.log({ database })
            if (database === null) {
                setLoading(true)
            } else {
                setLoading(false)
            }
        }, [database]);


        const search = () => {
            if (database) {
                console.log({ location, selectedFilters, distance, mode, term })
                let filterString = '';
                Object.entries(selectedFilters).forEach(
                    (kvp) => (filterString += `${filterString ? '&' : ''}${kvp[0]}=${Array.isArray(kvp[1]) ? kvp[1].map(encodeURIComponent).reduce((p, c) => p += `${p ? `&${kvp[0]}=` : ''}${c}`, '') : kvp[1]}`)
                );
                ReactGA.pageview(`${database.title}?term=${term}${filterString}`, [
                    'harmonize',
                    'client',
                ]);

                const {
                    map: mapKey,
                }: any = props.database ? getSepcialPageFieldKeysForDataabse(props.database) : {};

                let q = ''
                if (mode === 'keyword') q = `term=${term}`
                else if (mapKey && location?.formatted_address) {
                    const lat = location?.geometry?.location?.lat()
                    const lng = location?.geometry?.location?.lng()
                    q = `lat=${lat}&lng=${lng}&address=${encodeURIComponent(location.formatted_address)}&distance=${distance || 1}&${mapKey}=~${lat},${lng},${Math.floor((distance || 1) * 1609.34)}`
                }

                props.history.push(
                    `${SEARCH_PATH(database.name, props.accountName)}?${q}${filterString ? `&${filterString}` : ''}`,
                    { code }
                );
            }
        };

        const setFilter = (fieldName: string, value: string | string[]) => {
            const prev = Array.isArray(selectedFilters[fieldName]) ? selectedFilters[fieldName] : [];
            let next: string[];

            if (prev.includes(value)) {
                // Remove the value
                next = prev.filter(v => v !== value);
            } else {
                // Add the value
                next = [...prev, value];
            }

            // Update the filters
            const updatedFilters = { ...selectedFilters };
            if (next.length > 0) {
                updatedFilters[fieldName] = next;
            } else {
                // Remove field if none selected
                delete updatedFilters[fieldName];
            }
            setSelectedFilters(updatedFilters);
        };

        const handleKeyDown = (e: any) => {
            if (e.key === 'Enter') {
                search();
            }
        };

        const renderPoweredBy = () => {
            if (window.location.pathname.includes(EMBED_PATH)) {
                return (
                    <div className="col-lg-12 poweredby">
                        <a href={SITE_URL} target="_blank">
                            <span>
                                <FormattedMessage
                                    id="search.form.poweredby"
                                    defaultMessage="Powered by"
                                />
                            </span>
                            <img src={logoImage} width="100" />
                        </a>
                    </div>
                );
            } else {
                return '';
            }
        };

        const renderInlineFilters = () => {
            const filters = database && database.filters ? database.filters : [];
            const renderFilters = filters.slice(0, 2);
            // if showing expanded view, hide inline filters
            if (showFilters) return null;
            return (
                <React.Fragment>
                    {renderFilters.map((filter, idx) => {
                        // @ts-ignore
                        const field = database.fields.find(
                            (f) => format(f.group, f.name) === filter
                        );
                        if (!field) return null;
                        return (
                            <div className="col-md-auto" key={idx}>
                                <label htmlFor={field.name} className="sr-only">
                                    {field.label}
                                </label>
                                <select
                                    id={field.name}
                                    className="custom-select"
                                    onChange={($this) =>
                                        setFilter(filter, $this.currentTarget.value)
                                    }>
                                    <option key="0" value="">
                                        {field.label}
                                    </option>
                                    {field.options.map((o) => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                </React.Fragment>
            );
        };

        /**
         * Show expanded filters view
         */
        const renderSearchFilters = () => {
            const filters = database && database.filters ? database.filters : [];
            if (!showFilters && filters.length > 2)
                return (
                    <div className="col-lg-12 showmorefilters">
                        <button
                            className="btn btn-link text-harmonize-blue"
                            onClick={() => setShowFilters(true)}>
                            <FormattedMessage
                                id="search.form.filters"
                                defaultMessage="Show more filters"
                            />
                        </button>
                    </div>
                );
            else if (showFilters) {
                return (
                    <div className="col-lg-12 search-filters mt-3">
                        <FormattedMessage
                            tagName="h2"
                            id="search.filters.hdr"
                            defaultMessage="Filter results by"
                        />
                        <hr />
                        <div className="row mt-1">
                            {!database ||
                                database.filters.map((x) => {
                                    const field = database.fields.find(
                                        (f) => format(f.group, f.name) === x
                                    );
                                    if (!field) return null;
                                    return (
                                        <CommonSearchFormatter key={x} id={x} layout={database.settings.layout} field={field} onChange={setFilter}/>
                                    );
                                })}
                        </div>
                    </div>
                );
            } else return null;
        };

        if (loading) return (
            <div className="pt-5 d-flex flex-fill justify-content-center">
                <PropagateLoader size={15} loading={loading} color="#29447b" />
            </div>
        )

        if (props.database !== null && props.database.settings.layout !== 'brayn') {
            return (
                <div className="d-flex flex-row flex-fill wrap">
                    <div className="container search-container">
                        <main role="main" id="content" tabIndex={-1}>
                            <div className="row">
                                <div className="col-lg-12 spage-title">
                                    <h1>
                                        <FormattedMessage
                                            id="search.form.title"
                                            defaultMessage="Search"
                                        />{' '}
                                        {database ? database.title : ''}
                                    </h1>
                                    {/* <p><FormattedMessage id="search.form.text" defaultMessage="Description text if required. If not then we can get rid of this text message" /></p> */}
                                </div>
                                <div className="col-lg-12">
                                    <div className="row searchcol">
                                        <div className="col">
                                            <label className="sr-only" htmlFor="searchBar">
                                                {props.intl.formatMessage(messages.searchPrompt)}
                                            </label>
                                            <input
                                                id="searchBar"
                                                type="text"
                                                placeholder={props.intl.formatMessage(
                                                    messages.searchPrompt
                                                )}
                                                onKeyDown={handleKeyDown}
                                                onChange={(e) => setTerm(e.currentTarget.value)}
                                                value={term}
                                            />
                                        </div>
                                        {renderInlineFilters()}
                                        <div className="col-md-auto px-0 text-center">
                                            <button className="primary" onClick={() => search()}>
                                                <FormattedMessage
                                                    id="search.form.btn"
                                                    defaultMessage="Search"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {renderSearchFilters()}
                                {renderPoweredBy()}
                            </div>
                        </main>
                    </div>
                </div>
            )
        }

        return (
            <BraynSearch2
                intl={props.intl}
                renderPoweredBy={renderPoweredBy}
                handleKeyDown={handleKeyDown}
                setTerm={setTerm}
                term={term}
                search={search}
                setFilter={setFilter}
                database={database}
                filter={selectedFilters}
                setLocation={setLocation}
                location={location}
                distance={distance}
                setDistance={setDistance}
                mode={mode}
                setMode={setMode}
            />
        )
    }
);



// WEBPACK FOOTER //
// ./src/public/search/Search.tsx